import React, { useState } from "react";
import { Link } from "react-router-dom";
import { formatPhoneNumber } from "@/lib/utils";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Phone, AlertCircle, Calendar, User } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { format } from "date-fns";
import { safeFormat } from '@/lib/safeDate';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";

const statusColumns = {
  new: { title: "New", color: "bg-blue-100 text-blue-700 border-blue-200", bgColor: "bg-blue-50", borderColor: "border-blue-100" },
  contacted: { title: "Contacted", color: "bg-purple-100 text-purple-700 border-purple-200", bgColor: "bg-purple-50", borderColor: "border-purple-100" },
  qualifying: { title: "Qualifying", color: "bg-amber-100 text-amber-700 border-amber-200", bgColor: "bg-amber-50", borderColor: "border-amber-100" },
  qualified: { title: "Qualified", color: "bg-emerald-100 text-emerald-700 border-emerald-200", bgColor: "bg-emerald-50", borderColor: "border-emerald-100" },
  not_qualified: { title: "Not Qualified", color: "bg-red-100 text-red-700 border-red-200", bgColor: "bg-red-50", borderColor: "border-red-100" }
};

const notConvertedReasons = [
  "Fear/Insecurity",
  "Ghosted/No Response",
  "Indirect Inquiry",
  "Invalid Lead",
  "Needs Approval",
  "No Budget",
  "No Urgency",
  "Not Eligible",
  "Seasonal/Timing",
  "Went with Competitor"
];

export default function LeadKanban({ 
  leads, 
  isLoading, 
  onViewLead, 
  onStatusChange, 
  kanbanColumnsFilter = "all",
  selectedLeadIds = [],
  onToggleSelect
}) {
  const safeLeads = Array.isArray(leads) ? leads : [];
  const { toast } = useToast();
  const [pendingMove, setPendingMove] = useState(null);
  const [notConvertedReason, setNotConvertedReason] = useState("");
  
  const finalStatuses = ['qualified', 'not_qualified'];
  
  // Filter statuses based on kanbanColumnsFilter
  const getFilteredStatuses = () => {
    const naoAtivas = ['qualified', 'not_qualified'];
    const ativas = Object.keys(statusColumns).filter(status => !naoAtivas.includes(status));
    
    if (kanbanColumnsFilter === "all") {
      return Object.keys(statusColumns);
    }
    if (kanbanColumnsFilter === "nao_ativas") {
      return naoAtivas;
    }
    return ativas;
  };

  const filteredStatuses = getFilteredStatuses();
  
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    // Disable drag when items are selected
    if (selectedLeadIds.length > 0) {
      toast({
        title: "Drag desabilitado",
        description: "Limpe a seleção para usar drag and drop.",
        variant: "destructive",
      });
      return;
    }
    
    const leadId = result.draggableId;
    const newStatus = result.destination.droppableId;
    const lead = safeLeads.find(l => l.id === leadId);
    
    if (!lead || lead.status === newStatus) return;
    
    // Block movement of leads already in final status
    if (finalStatuses.includes(lead.status)) {
      toast({
        title: "Movement blocked",
        description: "Qualified or not qualified leads cannot be moved.",
        variant: "destructive",
      });
      return;
    }
    
    // If moving to final status, ask for confirmation
    if (finalStatuses.includes(newStatus)) {
      setPendingMove({ lead, newStatus });
    } else {
      onStatusChange?.(lead, newStatus);
    }
  };
  
  const confirmMove = () => {
    if (pendingMove) {
      // If not_qualified, validate that a reason has been selected
      if (pendingMove.newStatus === 'not_qualified' && !notConvertedReason) {
        toast({
          title: "Reason required",
          description: "Please select a reason for not converting.",
          variant: "destructive",
        });
        return;
      }
      
      onStatusChange?.(pendingMove.lead, pendingMove.newStatus, notConvertedReason);
      setPendingMove(null);
      setNotConvertedReason("");
    }
  };
  
  const cancelMove = () => {
    setPendingMove(null);
    setNotConvertedReason("");
  };

  const getLeadsByStatus = (status) => {
    return safeLeads.filter(lead => lead.status === status);
  };

  const handleSelectAllInColumn = (status) => {
    const columnLeads = getLeadsByStatus(status);
    const columnLeadIds = columnLeads.map(lead => lead.id);
    const allSelected = columnLeadIds.every(id => selectedLeadIds.includes(id));
    
    if (allSelected) {
      // Desselecionar todos desta coluna
      columnLeadIds.forEach(id => {
        if (selectedLeadIds.includes(id)) {
          onToggleSelect(id);
        }
      });
    } else {
      // Selecionar todos desta coluna
      columnLeadIds.forEach(id => {
        if (!selectedLeadIds.includes(id)) {
          onToggleSelect(id);
        }
      });
    }
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {filteredStatuses.map((status) => (
          <div key={status} className="space-y-3">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4 h-[calc(100dvh-244px)] md:h-[calc(100dvh-180px)]">
        {Object.entries(statusColumns)
          .filter(([status]) => filteredStatuses.includes(status))
          .map(([status, config]) => {
          const columnLeads = getLeadsByStatus(status);
          
          return (
            <div key={status} className="flex flex-col flex-shrink-0 w-80 h-full">
              {/* Column Header */}
              <div className={`${config.bgColor} ${config.borderColor} border rounded-t-lg px-4 py-3 flex items-center justify-between gap-2`}>
                <div className="flex items-center gap-2 flex-1">
                  {onToggleSelect && columnLeads.length > 0 && (
                    <Checkbox
                      checked={columnLeads.length > 0 && columnLeads.every(lead => selectedLeadIds.includes(lead.id))}
                      onCheckedChange={() => handleSelectAllInColumn(status)}
                      className="flex-shrink-0"
                    />
                  )}
                  <h3 className="text-lg font-bold text-slate-900">{config.title}</h3>
                </div>
                <Badge variant="outline" className={`${config.color} border font-medium text-xs flex-shrink-0`}>
                  {columnLeads.length}
                </Badge>
              </div>

              {/* Column Content */}
              <Droppable droppableId={status}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`${config.bgColor} ${config.borderColor} border-x border-b rounded-b-lg flex-1 space-y-3 p-3 transition-colors overflow-y-auto ${
                      snapshot.isDraggingOver ? "border-2 border-blue-400" : ""
                    }`}
                  >
                    {columnLeads.map((lead, index) => {
                      const isLocked = finalStatuses.includes(lead.status);
                      const isSelected = selectedLeadIds.includes(lead.id);
                      
                      return (
                        <Draggable 
                          key={lead.id} 
                          draggableId={lead.id} 
                          index={index}
                          isDragDisabled={isLocked || selectedLeadIds.length > 0}
                        >
                          {(provided, snapshot) => (
                            <Link 
                              to={`/lead-detail?id=${lead.id}`}
                              onClick={(e) => {
                                // Verifica se clicou no checkbox ou em qualquer parte dele
                                const clickedCheckbox = e.target.closest('[role="checkbox"]') || 
                                                        e.target.closest('[data-state]') ||
                                                        e.target.closest('.checkbox-wrapper');
                                
                                if (clickedCheckbox) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  return;
                                }
                                
                                // Se não for Ctrl/Cmd+Click, previne navegação padrão e usa SPA
                                if (!e.ctrlKey && !e.metaKey) {
                                  e.preventDefault();
                                  if (!snapshot.isDragging) {
                                    onViewLead(lead.id);
                                  }
                                }
                                // Se for Ctrl/Cmd+Click, deixa o Link fazer navegação em nova aba
                              }}
                              className="block"
                            >
                              <Card
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`bg-white border-slate-200 hover:shadow-md transition-shadow ${
                                  isLocked ? "opacity-75 cursor-default" : "cursor-pointer"
                                } ${isSelected ? "ring-2 ring-primary border-primary" : ""} ${
                                  snapshot.isDragging ? "shadow-xl rotate-2" : ""
                                }`}
                              >
                                <CardContent className="p-4 space-y-3">
                                  {/* Checkbox */}
                                  {onToggleSelect && !isLocked && (
                                    <div 
                                      className="flex justify-end checkbox-wrapper"
                                    >
                                      <Checkbox
                                        checked={isSelected}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          onToggleSelect(lead.id);
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onToggleSelect(lead.id);
                                          }
                                        }}
                                        aria-label="Select lead"
                                      />
                                    </div>
                                  )}
                                  {/* Lead Name */}
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h4 className="text-sm font-medium text-slate-900 leading-tight">
                                        {lead.first_name} {lead.last_name}
                                      </h4>
                                      {lead.market && (
                                        <Badge 
                                          variant="outline" 
                                          className={`border-0 font-medium text-xs ${
                                            lead.market === 'US' 
                                              ? 'bg-blue-100 text-blue-700' 
                                              : lead.market === 'BR' 
                                              ? 'bg-green-100 text-green-700'
                                              : 'bg-gray-100 text-gray-700'
                                          }`}
                                        >
                                          {lead.market}
                                        </Badge>
                                      )}
                                    </div>
                                    {isLocked && (
                                      <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                                    )}
                                  </div>

                                  {/* Phone */}
                                  {lead.phone && (
                                    <div className="flex items-center gap-2 text-slate-600">
                                      <Phone className="w-4 h-4 flex-shrink-0" />
                                      <span className="text-sm">{formatPhoneNumber(lead.phone)}</span>
                                    </div>
                                  )}
                                  
                                  {/* Created Date */}
                                  {lead.created_date && (
                                    <div className="flex items-center gap-2 text-slate-500">
                                      <Calendar className="w-4 h-4 flex-shrink-0" />
                                      <span className="text-xs">
                                        {safeFormat(new Date(lead.created_date), 'MMM dd, yyyy h:mm a')}
                                      </span>
                                    </div>
                                  )}

                                  {/* Assigned To */}
                                  <div className="flex items-center gap-2 text-slate-600 pt-2 border-t border-slate-100">
                                    <User className="w-4 h-4 flex-shrink-0" />
                                    <span className="text-xs font-medium">
                                      {lead.assigned_to_name || 'Unassigned'}
                                    </span>
                                  </div>
                                </CardContent>
                              </Card>
                            </Link>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                    
                    {/* Empty State */}
                    {columnLeads.length === 0 && (
                      <div className="flex items-center justify-center h-32 text-slate-400 text-sm">
                        No leads yet
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
        </div>
      </DragDropContext>

      {/* Confirmation Dialog */}
      <AlertDialog open={!!pendingMove} onOpenChange={cancelMove}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Confirmar movimentação
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                Você está prestes a mover este lead para{" "}
                <strong>
                  {pendingMove?.newStatus === 'qualified' ? 'Qualificado' : 'Não Qualificado'}
                </strong>.
              </p>
              <p className="text-amber-600 font-medium">
                ⚠️ Uma vez movido para este status, o lead não poderá ser movido novamente.
              </p>
              
              {pendingMove?.newStatus === 'not_qualified' && (
                <div className="space-y-2 pt-2">
                  <Label htmlFor="not-converted-reason" className="text-foreground">
                    Motivo da Não Conversão *
                  </Label>
                  <Select value={notConvertedReason} onValueChange={setNotConvertedReason}>
                    <SelectTrigger id="not-converted-reason" className="bg-background">
                      <SelectValue placeholder="Selecione um motivo" />
                    </SelectTrigger>
                    <SelectContent className="bg-background z-50">
                      {notConvertedReasons.map((reason) => (
                        <SelectItem key={reason} value={reason}>
                          {reason}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <p>Deseja continuar?</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelMove}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmMove}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}